import React, { useState, useEffect } from 'react';
import {
  FormControl,
  Select,
  OutlinedInput,
  Checkbox,
  ListItemText,
  MenuItem,
  FormHelperText,
  Typography,
} from '@mui/material';
import { isMobileOnly } from 'react-device-detect';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const MuiMultiSelectListDropdown = ({
  label,
  options,
  onChange,
  value,
  defaultValue = [],
  widthValue,
  fullWidth = false,
  customSx = {},
  required = false,
  errorMessage,
  MenuProps,
  customStyle = null,
  disabled = false,
}) => {
  const [internalSelectedValue, setInternalSelectedValue] = useState(
    Array.isArray(value) ? value : defaultValue
  );

  const [touched, setTouched] = useState(false);
  const [error, setError] = useState(false);

  const [userHasMadeSelection, setUserHasMadeSelection] = useState(false);

  useEffect(() => {
    const propValueArray = Array.isArray(value) ? value : [];

    if (JSON.stringify(propValueArray) !== JSON.stringify(internalSelectedValue)) {
      setInternalSelectedValue(propValueArray);
      setUserHasMadeSelection(false);
    }
  }, [value]);

  const handleSelectChange = (event) => {
    const selectedValueArray = event.target.value;
    setInternalSelectedValue(selectedValueArray);
    setUserHasMadeSelection(true);

    if (typeof onChange === 'function') {
      const validSelectedValueArray = selectedValueArray.filter(val =>
        options.some(option => option.value === val)
      );
      onChange(validSelectedValueArray);
    }

    if (required) {
      setError(selectedValueArray.length === 0);
    }
  };

  const handleClose = () => {
    if (required) {
      setTouched(true);
      if (!internalSelectedValue || (Array.isArray(internalSelectedValue) && internalSelectedValue.length === 0)) {
        setError(true);
      } else {
        setError(false);
      }
    }
  };

  const hasSelection = Array.isArray(internalSelectedValue) && internalSelectedValue.length > 0;
  const selectedCount = Array.isArray(internalSelectedValue) ? internalSelectedValue.length : 0;
  const displayText = hasSelection && selectedCount > 0
    ? `${label} (${selectedCount})` // Show label with count if items are selected
    : (label || ''); // Otherwise, just show the label

  const menuItemStyles = {
    display: 'flex',
    height: '56px',
    minHeight: '56px',
    paddingLeft: '16px',
    paddingRight: '16px',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: '16px',
  };

  // Define a consistent color variable
  const unifiedTextColor = 'var(--M3-sys-light-on-surface, #191C1C)'; // Or choose any color you prefer, e.g., '#333'

  return (
    <FormControl
      sx={{
        width: fullWidth ? '100%' : (isMobileOnly ? (widthValue ? '100%' : 'auto') : 'auto'),
        paddingRight: isMobileOnly && widthValue ? '20px' : '0px',
        ...customSx,
        ...(customStyle ? customStyle : {}),
      }}
      required={required}
      error={error}
      variant="outlined"
      disabled={disabled}
    >
      <Select
        id="multi-select-field"
        multiple
        value={Array.isArray(internalSelectedValue) ? internalSelectedValue : []}
        onChange={handleSelectChange}
        onClose={handleClose}
        input={<OutlinedInput />}
        IconComponent={ArrowDropDownIcon}
        displayEmpty
        renderValue={(selected) => {
          return (
            <Typography
              sx={{
                color: hasSelection
                  ? unifiedTextColor // Use the unified color here
                  : 'var(--M3-sys-light-on-surface-variant, #3F4949)',
                fontFamily: 'Inter',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '500',
                lineHeight: '20px',
                letterSpacing: '0.1px',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {displayText}
            </Typography>
          );
        }}
        sx={{
          width: fullWidth ? '100%' : (isMobileOnly ? '100%' : 'auto'),
          height: '32px',
          '.MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.5) !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#00696D !important',
          },
          '.MuiSelect-select': {
            display: 'flex',
            height: '100% !important',
            padding: '6px 8px 6px 16px',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '8px',
            boxSizing: 'border-box',
          },
          '.MuiSelect-icon': {
            color: unifiedTextColor, // Apply the unified color to the icon
            right: '8px',
          },
          '& .MuiOutlinedInput-root': {
            paddingTop: '0px !important',
            paddingBottom: '0px !important',
            borderRadius: '8px'
          }
        }}
        MenuProps={{
          ...MenuProps,
          PaperProps: {
            ...MenuProps?.PaperProps,
            style: {
              maxHeight: '200px',
              ...MenuProps?.PaperProps?.style,
            },
            sx: {
              ...MenuProps?.PaperProps?.sx,
              '& .MuiMenuItem-root .MuiListItemText-primary': {
                color: unifiedTextColor, // Apply the unified color to primary text
                fontFamily: 'Inter !important',
                fontSize: '16px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: '24px !important',
                letterSpacing: '0.5px !important'
              },
              '& .MuiMenuItem-root .MuiTypography-root': {
                color: unifiedTextColor, // Apply the unified color to Typography within MenuItem
                fontFamily: 'Inter !important',
                fontSize: '16px !important',
                fontStyle: 'normal !important',
                fontWeight: '400 !important',
                lineHeight: '24px !important',
                letterSpacing: '0.5px !important'
              }
            }
          }
        }}
      >
        {options?.map(({ label: optionLabel, value: optionValue }) => (
          <MenuItem
            key={optionValue}
            value={optionValue}
            dense
            sx={{
              ...menuItemStyles,
              '&.Mui-selected': {
                backgroundColor: 'transparent !important',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
              }
            }}
          >
            <Checkbox
              checked={(Array.isArray(internalSelectedValue) ? internalSelectedValue : []).includes(optionValue)}
              sx={{
                color: '#6F7979',
                '&.Mui-checked': { color: '#00696D' },
              }}
            />
            <ListItemText
              primary={optionLabel}
              sx={{
                flexGrow: 1,
                color: unifiedTextColor, // Apply the unified color here as well
                fontFamily: 'Inter',
                fontSize: '16px',
                fontStyle: 'normal',
                fontWeight: '400',
                lineHeight: '24px',
                letterSpacing: '0.5px'
              }}
            />
          </MenuItem>
        ))}
      </Select>

      {error && (
        <FormHelperText>{errorMessage || 'Dette feltet er påkrevd'}</FormHelperText>
      )}
    </FormControl>
  );
};

export default MuiMultiSelectListDropdown;